import { AppLayout } from '@/components/AppLayout';
import { ServicePageGuard } from '@/components/ServicePageGuard';
import { SERVICE_KEYS } from '@/lib/serviceKeys';
import { HIRE_LIST_HEADER_FLOW, HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING, HIRE_STAT_GRID, HIRE_STAT_PAD, HIRE_PAGE_HEADER_PAD } from '@/lib/hireResponsive';
import { Card } from '@/components/ui/card';
import { StageChangeConfirmDialog, type PendingStageChange } from '@/components/StageChangeConfirmDialog';
import { StageBadge } from '@/components/StageBadge';
import { DualScoreBadge } from '@/components/DualScoreBadge';
import { ApplicationAiReviewSummary } from '@/components/jobs/ApplicationAiReviewSummary';
import { useCandidates, useUpdateApplicationStage } from '@/hooks/useCandidates';
import { useCandidateAvatars } from '@/hooks/useCandidateAvatars';
import { useServiceAccess } from '@/hooks/useServiceAccess';
import { useJobInterviewReady } from '@/hooks/useJobInterviewReady';
import { useJobGovernance } from '@/hooks/useJobGovernance';
import { Search, Filter, ChevronRight, ChevronDown, Users, X, Star, TrendingUp, UserCheck, Download, Sparkles, Loader2, MapPin, Clock, Briefcase, UserRound, Eye, Shield, Lock } from 'lucide-react';
import { CvScoringDialog } from '@/components/CvScoringDialog';
import { SendInterviewLinkButton } from '@/components/SendInterviewLinkButton';
import { AssignRecruiterSelect } from '@/components/AssignRecruiterSelect';
import { DisqualifyCandidateAction } from '@/components/pipeline/DisqualifyCandidateAction';
import { GovernedAdvanceDialog } from '@/components/pipeline/GovernedAdvanceDialog';
import { LateStageWorkflowActions } from '@/components/pipeline/LateStageWorkflowActions';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { stageLabels } from '@/data/stageConfig';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { SecondInterviewDialog } from '@/components/SecondInterviewDialog';
import { F2FPreviewDialog, type F2FDetails } from '@/components/F2FPreviewDialog';
import { formatCandidateId, formatCandidateName } from '@/lib/candidateId';
import { ForkSeekAccountBadge } from '@/components/ForkSeekAccountBadge';
import { formatJobId } from '@/lib/jobId';
import { formatLocalizedDate } from '@/lib/localizedDate';
import { detectTextDirection } from '@/lib/textDirection';
import { usePermissions } from '@/hooks/usePermissions';
import { isForkHireCandidateDetailUnlocked } from '@/lib/forkhire/workflow/candidateDetailUnlock';
import { resolveForkHireContactDisplay } from '@/lib/forkhire/workflow/contactDisplay';
import { buildDisqualifyStageMutation, DISQUALIFY_COPY } from '@/lib/forkhire/workflow/candidateDisqualification';
import { isForkHireGlobalCandidateVisible } from '@/lib/forkhire/workflow/globalCandidateVisibility';
import { buildForkHireCandidateApplicationPath } from '@/lib/forkhire/workflow/candidateApplicationRoute';
import { isDirectAiInterviewCompletionAdvance } from '@/lib/forkhire/interview/interviewStageActivation';
import { f2fDialogSuccessCallbacks } from '@/lib/forkhire/workflow/f2fDialogMutation';
import { validF2FScheduleDetails } from '@/lib/forkhire/workflow/f2fScheduleDetails';
import { resolveCvReviewAdvanceTarget } from '@/lib/forkhire/workflow/manualCvReviewShortlist';
import {
  createGovernedOperationKey,
  useGovernedStageOperation,
  useLateStageSummaries,
} from '@/hooks/useGovernedStageOperation';
import { isLateStagePipelineStage } from '@/lib/forkhire/workflow/lateStageWorkflow';
import { RESPONSIVE_ACTION_ROW, RESPONSIVE_BUTTON_TEXT, RESPONSIVE_HEADER_ROW, RESPONSIVE_METRIC_CONTENT, SAFE_COMPACT_VALUE, SAFE_TEXT_WRAP } from '@/lib/responsiveText';

const orderedStages = ['applied', 'ai_reviewed', 'shortlisted', 'interviewed', 'second_interview', 'offering', 'signing_contract', 'hired'];
const reviewedPassedStages = ['shortlisted', 'interviewed', 'second_interview', 'offering', 'signing_contract', 'hired'];

function getAdjacentStages(stage: string, hasAiInterviews = true) {
  const stages = hasAiInterviews
    ? orderedStages
    : orderedStages.filter(s => s !== 'interviewed');
  const idx = stages.indexOf(stage);
  return {
    next: idx >= 0 && idx < stages.length - 1 ? stages[idx + 1] : null,
  };
}

const INITIAL_SHOW = 5;

export default function Candidates() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const [search, setSearch] = useState('');
  const [jobStatusTab, setJobStatusTab] = useState('active');
  const [stageFilter, setStageFilter] = useState('all');
  const [scoreFilter, setScoreFilter] = useState('all');
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});
  const { data: candidates, isLoading } = useCandidates();
  const candidateIds = useMemo(() => (candidates || []).map(c => c.id), [candidates]);
  const { data: avatarMap } = useCandidateAvatars(candidateIds);
  const updateStage = useUpdateApplicationStage();
  const [scoringDialogOpen, setScoringDialogOpen] = useState(false);
  const { hasAccess: hasCvScoringAccess } = useServiceAccess(SERVICE_KEYS.AI_CV_SCORING);
  const { hasAccess: hasAiInterviewsService } = useServiceAccess(SERVICE_KEYS.AI_INTERVIEWS);
  const [pendingStageChange, setPendingStageChange] = useState<PendingStageChange | null>(null);
  const [secondInterviewPending, setSecondInterviewPending] = useState<{ applicationId: string; candidateName: string; fromStage: string } | null>(null);
  const [f2fPreview, setF2fPreview] = useState<{ candidateName: string; details: F2FDetails; applicationId: string; jobTitle: string } | null>(null);
  const [pendingGovernedAdvance, setPendingGovernedAdvance] = useState<{
    applicationId: string;
    jobId: string;
    fromStage: string;
    candidateName: string;
  } | null>(null);
  const { can: hasPerm } = usePermissions();
  const canViewPii = hasPerm('candidates', 'view_sensitive');
  const governedStageOperation = useGovernedStageOperation();
  const {
    data: pendingAdvanceCapacity,
    isFetched: pendingAdvanceCapacityReady,
  } = useJobGovernance(pendingGovernedAdvance?.jobId);

  useEffect(() => {
    if (!pendingGovernedAdvance || !pendingAdvanceCapacityReady) return;
    if (!pendingAdvanceCapacity?.governed) {
      setPendingStageChange({
        applicationId: pendingGovernedAdvance.applicationId,
        fromStage: pendingGovernedAdvance.fromStage,
        toStage: 'shortlisted',
        candidateName: pendingGovernedAdvance.candidateName,
      });
      setPendingGovernedAdvance(null);
    }
  }, [pendingAdvanceCapacity, pendingAdvanceCapacityReady, pendingGovernedAdvance]);

  const rows = useMemo(() => (candidates || []).flatMap((c) =>
    c.applications
      .filter(isForkHireGlobalCandidateVisible)
      .map((app) => ({ candidate: c, app }))
  ), [candidates]);

  const allJobIds = useMemo(() => [...new Set(rows.map(r => r.app.job_id))], [rows]);
  const { data: interviewReadyJobs } = useJobInterviewReady(allJobIds);

  const activeRows = useMemo(() => rows.filter(r => r.app.jobs?.status === 'active'), [rows]);

  const inactiveRows = useMemo(() => rows.filter(r => r.app.jobs?.status === 'inactive'), [rows]);

  const closedRows = useMemo(() => rows.filter(r => {
    const jobStatus = r.app.jobs?.status;
    return jobStatus === 'closed' || jobStatus === 'expired';
  }), [rows]);

  const currentRows = jobStatusTab === 'active' ? activeRows : jobStatusTab === 'inactive' ? inactiveRows : closedRows;

  const filteredRows = useMemo(() => currentRows.filter((r) => {
    const candidateIdStr = formatCandidateId(r.candidate.candidate_number, r.candidate.seek_candidate_number).toLowerCase();
    const matchesSearch = !search || r.candidate.name.toLowerCase().includes(search.toLowerCase()) || candidateIdStr.includes(search.toLowerCase()) || String(r.candidate.candidate_number || '').includes(search);
    const matchesStage = stageFilter === 'all' || r.app.stage === stageFilter;
    const score = r.app.cv_scores?.total_score;
    const matchesScore = scoreFilter === 'all'
      || (scoreFilter === '90' && score != null && score >= 90)
      || (scoreFilter === '70' && score != null && score >= 70)
      || (scoreFilter === '50' && score != null && score >= 50)
      || (scoreFilter === 'unscored' && score == null);
    return matchesSearch && matchesStage && matchesScore;
  }), [currentRows, search, stageFilter, scoreFilter]);
  const lateStageIds = useMemo(
    () => filteredRows.filter((row) => isLateStagePipelineStage(row.app.stage)).map((row) => row.app.id),
    [filteredRows],
  );
  const lateStageSummaries = useLateStageSummaries(lateStageIds);

  const jobGroups = useMemo(() => {
    const grouped = filteredRows.reduce<Record<string, { jobTitle: string; department: string | null; jobId: string; jobStatus: string; location: string | null; employmentType: string | null; createdAt: string | null; jobNumber: number | null; rows: typeof filteredRows }>>((acc, r) => {
      const jobId = r.app.job_id;
      const jobTitle = r.app.jobs?.title || t('hire.candidates.unknownJob');
      const department = r.app.jobs?.department || null;
      if (!acc[jobId]) acc[jobId] = {
        jobTitle, department, jobId,
        jobStatus: r.app.jobs?.status || 'active',
        location: r.app.jobs?.location || null,
        employmentType: r.app.jobs?.employment_type || null,
        createdAt: r.app.jobs?.created_at || null,
        jobNumber: r.app.jobs?.job_number || null,
        rows: [],
      };
      acc[jobId].rows.push(r);
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.rows.length - a.rows.length);
  }, [filteredRows, t]);

  const toggleGroup = useCallback((jobId: string) => {
    setOpenGroups((prev) => ({ ...prev, [jobId]: !prev[jobId] }));
  }, []);

  const isOpen = (jobId: string) => openGroups[jobId] ?? (jobGroups[0]?.jobId === jobId);

  // KPI stats — scoped to current job-status tab
  const activeCandidates = currentRows.length;
  const highScoreCount = currentRows.filter((r) => (r.app.cv_scores?.total_score ?? 0) >= 90).length;
  const inInterviewCount = currentRows.filter((r) => r.app.stage === 'interviewed' || r.app.stage === 'second_interview').length;
  const totalHired = currentRows.filter((r) => r.app.stage === 'hired').length;

  const activeFilterCount = (search ? 1 : 0) + (stageFilter !== 'all' ? 1 : 0) + (scoreFilter !== 'all' ? 1 : 0);

  return (
    <AppLayout>
      <ServicePageGuard serviceKey={SERVICE_KEYS.CANDIDATE_PIPELINE}>
      <div className={`${HIRE_SECTION_SPACING} ${HIRE_PAGE_CONTAINER}`}>
        {/* Header Card — unified with KPI stats */}
        <div className={HIRE_LIST_HEADER_FLOW}>
        <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
          <div className={`${HIRE_PAGE_HEADER_PAD} ${RESPONSIVE_HEADER_ROW}`}>
            <div className="min-w-0 max-w-3xl">
              <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold font-display text-foreground ${SAFE_TEXT_WRAP}`}>{t('hire.candidates.title')}</h1>
              <p className={`text-muted-foreground mt-1.5 text-sm ${SAFE_TEXT_WRAP}`}>{t('hire.candidates.subtitle')}</p>
            </div>
            <div className={RESPONSIVE_ACTION_ROW}>
              <Button size="sm" className={`rounded-full px-5 press-scale text-sm font-medium ${RESPONSIVE_BUTTON_TEXT}`}
                onClick={() => setScoringDialogOpen(true)}
                disabled={!hasCvScoringAccess}>
                {hasCvScoringAccess ? <Sparkles className="w-4 h-4 me-1.5" /> : <Lock className="w-4 h-4 me-1.5" />} {t('hire.candidates.cvScoring')}
              </Button>
            </div>
          </div>

          {/* Stats row inside header */}
          <div className={`${HIRE_STAT_GRID} px-5 sm:px-6 lg:px-8 3xl:px-10 pb-5 sm:pb-6 lg:pb-8 3xl:pb-10`}>
            {[
              { icon: <Users className="w-5 h-5 text-primary" />, value: activeCandidates, label: t('hire.candidates.activeCandidates'), bg: 'bg-primary/10' },
              { icon: <Star className="w-5 h-5 text-amber-500" />, value: highScoreCount, label: t('hire.candidates.highScore'), bg: 'bg-amber-500/10' },
              { icon: <UserCheck className="w-5 h-5 text-success" />, value: inInterviewCount, label: t('hire.candidates.inInterview'), bg: 'bg-success/10' },
              { icon: <TrendingUp className="w-5 h-5 text-orange-500" />, value: totalHired, label: t('hire.candidates.totalHired'), bg: 'bg-orange-500/10' },
            ].map((stat) => (
              <div key={stat.label} className={`bg-muted/40 border border-border rounded-2xl ${HIRE_STAT_PAD}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-xl ${stat.bg} flex items-center justify-center flex-shrink-0`}>
                    {stat.icon}
                  </div>
                  <div className={RESPONSIVE_METRIC_CONTENT}>
                    <p className="text-2xl lg:text-3xl font-bold font-display text-foreground tabular-nums">{stat.value}</p>
                    <p className={`text-xs sm:text-sm text-muted-foreground font-medium mt-1 ${SAFE_COMPACT_VALUE}`}>{stat.label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col xl:flex-row xl:items-center gap-3 px-5 sm:px-8 py-4 border-t border-border">
            <div className="relative w-full xl:flex-1 xl:min-w-[220px]">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('hire.candidates.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-9 h-10 rounded-xl"
              />
            </div>
            {/* Active / Closed tabs */}
            <div className="flex items-center gap-1 bg-muted/50 border border-border rounded-xl p-1 flex-wrap xl:flex-nowrap">
              {[
                { key: 'active', label: t('stages.active'), count: activeRows.length },
                { key: 'inactive', label: t('stages.inactive'), count: inactiveRows.length },
                { key: 'closed', label: t('stages.closed'), count: closedRows.length },
              ].map((tabItem) => (
                <button
                  key={tabItem.key}
                  onClick={() => setJobStatusTab(tabItem.key)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${SAFE_TEXT_WRAP} ${
                    jobStatusTab === tabItem.key
                      ? 'bg-card text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tabItem.label}
                  <span className={`ms-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                    jobStatusTab === tabItem.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    {tabItem.count}
                  </span>
                </button>
              ))}
            </div>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-full xs:w-[150px] h-10 rounded-xl">
                <SelectValue placeholder={t('hire.candidates.allStages')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('hire.candidates.allStages')}</SelectItem>
                {reviewedPassedStages
                  .filter(s => hasAiInterviewsService || s !== 'interviewed')
                  .map((s) => (
                  <SelectItem key={s} value={s}>{t(`stages.${s}`, { defaultValue: s })}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={scoreFilter} onValueChange={setScoreFilter}>
              <SelectTrigger className="w-full xs:w-[150px] h-10 rounded-xl">
                <SelectValue placeholder={t('hire.candidates.allScores')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('hire.candidates.allScores')}</SelectItem>
                <SelectItem value="90">{t('hire.candidates.score90')}</SelectItem>
                <SelectItem value="70">{t('hire.candidates.score70')}</SelectItem>
                <SelectItem value="50">{t('hire.candidates.score50')}</SelectItem>
                <SelectItem value="unscored">{t('hire.candidates.unscored')}</SelectItem>
              </SelectContent>
            </Select>
            {activeFilterCount > 0 && (
              <Button variant="outline" size="sm" className="rounded-xl h-10 gap-1.5 text-destructive hover:text-destructive"
                onClick={() => { setSearch(''); setStageFilter('all'); setScoreFilter('all'); }}>
                <X className="w-4 h-4" /> {t('hire.candidates.clear')}
              </Button>
            )}
          </div>
        </Card>
        </div>

        {/* Job Groups */}
        {isLoading ? (
          <div className="space-y-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
        ) : jobGroups.length === 0 ? (
          <Card className="p-12 text-center shadow-card border-border rounded-xl">
            <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="font-medium text-foreground mb-1">{t('hire.candidates.noCandidatesTitle')}</p>
            <p className="text-muted-foreground text-sm mb-4">{t('hire.candidates.noCandidates')}</p>
            {activeFilterCount > 0 && (
              <Button variant="outline" size="sm" onClick={() => { setSearch(''); setStageFilter('all'); setScoreFilter('all'); }} className="rounded-xl">
                {t('hire.candidates.clearFilters')}
              </Button>
            )}
          </Card>
        ) : (
          <Card className="shadow-card border-border border-s-4 border-s-primary rounded-xl overflow-hidden divide-y divide-border">
            {jobGroups.map((group) => {
              const expanded = isOpen(group.jobId);
              const visibleRows = showAll[group.jobId] ? group.rows : group.rows.slice(0, INITIAL_SHOW);
              const hasMore = group.rows.length > INITIAL_SHOW;

              return (
                <Collapsible key={group.jobId} open={expanded} onOpenChange={() => toggleGroup(group.jobId)}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 hover:bg-muted/40 transition-colors group">
                    <CollapsibleTrigger asChild>
                      <button className="flex items-center justify-center w-6 h-6 rounded hover:bg-muted transition-colors flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      </button>
                    </CollapsibleTrigger>
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Briefcase className="w-4 h-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-semibold text-sm text-foreground ${SAFE_COMPACT_VALUE}`} dir={detectTextDirection(group.jobTitle)}>{group.jobTitle}</p>
                          {group.jobNumber && (
                            <span className="inline-flex items-center text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border whitespace-nowrap">{formatJobId(group.jobNumber)}</span>
                          )}
                          <StageBadge stage={group.jobStatus} />
                        </div>
                        <div className="flex items-center gap-x-4 gap-y-1 text-xs text-muted-foreground mt-1.5 flex-wrap">
                          {group.location && (
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="w-3 h-3 flex-shrink-0" /> {group.location}
                            </span>
                          )}
                          {group.department && (
                            <span className="inline-flex items-center gap-1">{group.department}</span>
                          )}
                          {group.employmentType && (
                            <span className="capitalize">{group.employmentType}</span>
                          )}
                          {group.createdAt && (
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3 flex-shrink-0" /> {formatLocalizedDate(group.createdAt, 'PP', language)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground flex-shrink-0">
                      <Users className="w-4 h-4" />
                      <span className="font-semibold">{group.rows.length}</span>
                    </div>
                  </div>

                  <CollapsibleContent>
                    <div className="bg-muted/30 border-t border-border">
                      <div className="divide-y divide-border/50">
                        {visibleRows.map((r) => {
                          const stage = r.app.stage || 'applied';
                          const score = r.app.cv_scores?.total_score;
                          const isRejected = stage === 'rejected';
                          const detailsUnlocked = isForkHireCandidateDetailUnlocked(stage);
                          const jobHasInterviewConfig = interviewReadyJobs?.has(r.app.job_id) ?? false;
                          const effectiveAiInterviews = hasAiInterviewsService && jobHasInterviewConfig;
                          const adjacent = getAdjacentStages(stage, effectiveAiInterviews);
                          const next = resolveCvReviewAdvanceTarget({
                            stage,
                            status: r.app.status,
                            cvScore: score,
                            defaultNext: adjacent.next,
                          });
                          const usesGovernedLateStageActions = isLateStagePipelineStage(stage);
                          const contact = resolveForkHireContactDisplay({
                            stage,
                            source: r.app.source || r.candidate.source,
                            jobStatus: r.app.jobs?.status,
                            canViewSensitive: canViewPii,
                            email: r.app.contact_email ?? r.candidate.email,
                            phone: r.app.contact_phone ?? r.candidate.phone,
                            location: r.app.contact_location ?? r.candidate.location,
                          });

                          return (
                            <div
                              key={r.app.id}
                              className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-2.5 ps-14 hover:bg-muted/30 transition-colors"
                            >
                              {/* Candidate info */}
                              <div className="flex items-start gap-2 flex-1 min-w-0">
                                <Link to={buildForkHireCandidateApplicationPath({ candidateId: r.candidate.id, applicationId: r.app.id })} className="flex items-center gap-3 flex-1 min-w-0 touch-target">
                                <div className={`w-10 h-10 rounded-xl border border-border flex items-center justify-center flex-shrink-0 overflow-hidden ${r.candidate.seek_candidate_number ? 'bg-success/10' : 'bg-primary/10'}`}>
                                    {avatarMap?.get(r.candidate.id) ? (
                                      <img src={avatarMap.get(r.candidate.id)} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <UserRound className={`w-4.5 h-4.5 ${r.candidate.seek_candidate_number ? 'text-success' : 'text-primary'}`} />
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                                      {(r.candidate.candidate_number || r.candidate.seek_candidate_number) && (
                                        <span className="inline-flex items-center text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border whitespace-nowrap">{formatCandidateId(r.candidate.candidate_number, r.candidate.seek_candidate_number)}</span>
                                      )}
                                      <p className={`font-medium text-sm sm:text-base text-foreground flex items-center gap-1 ${SAFE_COMPACT_VALUE}`} dir={detectTextDirection(r.candidate.name)}>{formatCandidateName(r.candidate.name)} <ForkSeekAccountBadge seekCandidateNumber={r.candidate.seek_candidate_number} /></p>
                                       {(r.app.source || r.candidate.source) === 'invited' ? (
                                         <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border border-yellow-500/30 whitespace-nowrap">{t('hire.candidates.invited')}</span>
                                       ) : (r.app.source || r.candidate.source) === 'public_application' || (r.app.source || r.candidate.source) === 'seek_ai' ? (
                                           <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20 whitespace-nowrap">{t('hire.candidates.submitted')}</span>
                                         ) : (
                                           <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 whitespace-nowrap">{t('hire.candidates.uploaded')}</span>
                                       )}
                                     </div>
                                    <p className={`text-xs sm:text-sm text-muted-foreground ${SAFE_COMPACT_VALUE}`}>
                                      {contact.label}{contact.sourceLabel ? ` - ${contact.sourceLabel}` : ''}
                                    </p>
                                    {!detailsUnlocked && (
                                      <ApplicationAiReviewSummary application={r.app} compact className="mt-1" />
                                    )}
                                  </div>
                                </Link>
                                <div className="pt-0.5 shrink-0">
                                  <AssignRecruiterSelect applicationId={r.app.id} assignedTo={r.app.assigned_to ?? null} compact />
                                </div>
                              </div>

                              {/* Stage controls + Score */}
                              <div className="ms-[52px] flex min-w-0 max-w-full flex-wrap items-center gap-2 sm:ms-0 sm:gap-3">
                                {(stage === 'shortlisted' || stage === 'interviewed') && (
                                  <SendInterviewLinkButton applicationId={r.app.id} stage={stage} jobId={r.app.job_id} candidateEmail={canViewPii && detailsUnlocked ? (contact.email ?? '') : ''} candidateName={r.candidate.name} compact />
                                )}
                                {stage === 'second_interview' && validF2FScheduleDetails(r.app.f2f_details_json) && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg border-primary/30 text-primary hover:bg-primary/5"
                                        onClick={() => {
                                          const details = validF2FScheduleDetails(r.app.f2f_details_json);
                                          if (details) {
                                            setF2fPreview({ candidateName: r.candidate.name, details, applicationId: r.app.id, jobTitle: r.app.jobs?.title || '' });
                                          }
                                        }}>
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">{t('hire.candidates.viewF2FDetails')}</TooltipContent>
                                  </Tooltip>
                                )}
                                <div className="flex flex-wrap items-center gap-1">
                                  <DisqualifyCandidateAction
                                    stage={stage}
                                    disabled={updateStage.isPending || (
                                      usesGovernedLateStageActions &&
                                      !lateStageSummaries.data?.[r.app.id]?.allowedOperations.includes('disqualify')
                                    )}
                                    isPending={updateStage.isPending && updateStage.variables?.applicationId === r.app.id}
                                    reason={
                                      usesGovernedLateStageActions &&
                                      !lateStageSummaries.data?.[r.app.id]?.allowedOperations.includes('disqualify')
                                        ? t(`hire.lateStage.reasons.${lateStageSummaries.data?.[r.app.id]?.denialReasons.disqualify ?? 'state_check_pending'}`)
                                        : null
                                    }
                                    onClick={() => {
                                      const mutation = buildDisqualifyStageMutation(r.app.id);
                                      setPendingStageChange({
                                        applicationId: mutation.applicationId,
                                        fromStage: stage,
                                        toStage: mutation.stage,
                                        status: mutation.status,
                                        candidateName: r.candidate.name,
                                        jobTitle: r.app.jobs?.title || '',
                                        confirmationTitle: DISQUALIFY_COPY.title,
                                        confirmationBody: DISQUALIFY_COPY.body,
                                        confirmLabel: DISQUALIFY_COPY.confirmLabel,
                                        cancelLabel: DISQUALIFY_COPY.cancelLabel,
                                      });
                                    }}
                                  />
                                  {usesGovernedLateStageActions && (
                                    <LateStageWorkflowActions
                                      applicationId={r.app.id}
                                      stage={stage}
                                      pipelineAllowed
                                      summary={lateStageSummaries.data?.[r.app.id]}
                                      summaryLoading={lateStageSummaries.isLoading}
                                      dense
                                      onScheduleF2F={() => setSecondInterviewPending({
                                        applicationId: r.app.id,
                                        candidateName: r.candidate.name,
                                        fromStage: stage,
                                      })}
                                      onMoveToF2F={() => updateStage.mutate({
                                        applicationId: r.app.id,
                                        stage: 'second_interview',
                                        expectedSourceStage: stage,
                                      })}
                                      onMoveToContract={() => updateStage.mutate({
                                        applicationId: r.app.id,
                                        stage: 'signing_contract',
                                        expectedSourceStage: stage,
                                      })}
                                    />
                                  )}
                                  <StageBadge stage={stage} />
                                  {!usesGovernedLateStageActions && !isDirectAiInterviewCompletionAdvance(stage, next, effectiveAiInterviews) && (
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" disabled={!next || isRejected || updateStage.isPending}
                                          onClick={() => {
                                            if (!next) return;
                                            if ((stage === 'applied' || stage === 'ai_reviewed') && next === 'shortlisted') {
                                              setPendingGovernedAdvance({
                                                applicationId: r.app.id,
                                                jobId: r.app.job_id,
                                                fromStage: stage,
                                                candidateName: r.candidate.name,
                                              });
                                              return;
                                            }
                                            if (next === 'second_interview') {
                                              setSecondInterviewPending({ applicationId: r.app.id, candidateName: r.candidate.name, fromStage: stage });
                                            } else {
                                              setPendingStageChange({ applicationId: r.app.id, fromStage: stage, toStage: next, candidateName: r.candidate.name });
                                            }
                                          }}>
                                          {(stage === 'applied' || stage === 'ai_reviewed') && next === 'shortlisted'
                                            ? <Shield className="w-4 h-4 text-primary rtl-flip" />
                                            : <ChevronRight className="w-4 h-4 rtl-flip" />}
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="text-xs">
                                        {(stage === 'applied' || stage === 'ai_reviewed') && next === 'shortlisted'
                                          ? t('hire.candidates.advanceToShortlistedGoverned')
                                          : next ? t('hire.candidates.moveTo', { stage: t(`stages.${next}`, { defaultValue: stageLabels[next] }) }) : t('hire.candidates.finalStage')}
                                      </TooltipContent>
                                    </Tooltip>
                                  )}
                                </div>
                                <Link to={buildForkHireCandidateApplicationPath({ candidateId: r.candidate.id, applicationId: r.app.id })} className="flex max-w-full justify-center sm:ms-1">
                                  <DualScoreBadge
                                    cvScore={score}
                                    interviewScore={(() => {
                                      const sessions = r.app.interview_sessions;
                                      if (!sessions || sessions.length === 0) return null;
                                      const scored = sessions.filter(s => s.overall_score != null);
                                      if (scored.length === 0) return null;
                                      return Math.max(...scored.map(s => s.overall_score!));
                                    })()}
                                  />
                                </Link>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {hasMore && (
                        <div className="border-t border-border/50 px-4 py-3 text-center">
                          <Button variant="ghost" size="sm" className="text-sm text-muted-foreground touch-target"
                            onClick={() => setShowAll((prev) => ({ ...prev, [group.jobId]: !prev[group.jobId] }))}>
                            {showAll[group.jobId] ? t('common.showLess') : t('common.showAll', { count: group.rows.length })}
                          </Button>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </Card>
        )}
      </div>
      <CvScoringDialog open={scoringDialogOpen} onOpenChange={setScoringDialogOpen} />
      <StageChangeConfirmDialog
        pending={pendingStageChange}
        isPending={updateStage.isPending || governedStageOperation.isPending}
        onConfirm={() => {
          if (pendingStageChange) {
            if (pendingStageChange.toStage === 'rejected') {
              governedStageOperation.mutate(
                {
                  applicationId: pendingStageChange.applicationId,
                  operation: 'disqualify',
                  idempotencyKey: createGovernedOperationKey('disqualify', pendingStageChange.applicationId),
                  reasonCode: 'manual_disqualification',
                },
                { onSettled: () => setPendingStageChange(null) }
              );
              return;
            }
            updateStage.mutate(
              {
                applicationId: pendingStageChange.applicationId,
                stage: pendingStageChange.toStage,
                expectedSourceStage: pendingStageChange.fromStage,
                status: pendingStageChange.status,
              },
              { onSettled: () => setPendingStageChange(null) }
            );
          }
        }}
        onCancel={() => setPendingStageChange(null)}
      />
      <GovernedAdvanceDialog
        open={!!pendingGovernedAdvance && pendingAdvanceCapacity?.governed === true}
        isPending={updateStage.isPending}
        fromStage={pendingGovernedAdvance?.fromStage ?? 'ai_reviewed'}
        toStage="shortlisted"
        candidateName={pendingGovernedAdvance?.candidateName ?? ''}
        capacity={pendingAdvanceCapacity}
        onCancel={() => setPendingGovernedAdvance(null)}
        onConfirm={() => {
          if (!pendingGovernedAdvance) return;
          updateStage.mutate(
            {
              applicationId: pendingGovernedAdvance.applicationId,
              stage: 'shortlisted',
              expectedSourceStage: pendingGovernedAdvance.fromStage,
            },
            { onSettled: () => setPendingGovernedAdvance(null) }
          );
        }}
      />
      <SecondInterviewDialog
        open={!!secondInterviewPending}
        onOpenChange={(open) => { if (!open) setSecondInterviewPending(null); }}
        candidateName={secondInterviewPending?.candidateName || ''}
        isPending={updateStage.isPending}
        onSubmit={(details) => {
          if (secondInterviewPending) {
            updateStage.mutate(
              {
                applicationId: secondInterviewPending.applicationId,
                stage: 'second_interview',
                expectedSourceStage: secondInterviewPending.fromStage,
                secondInterviewDetails: details,
              },
              f2fDialogSuccessCallbacks(() => setSecondInterviewPending(null))
            );
          }
        }}
      />
      <F2FPreviewDialog
        open={!!f2fPreview}
        onOpenChange={(open) => { if (!open) setF2fPreview(null); }}
        candidateName={f2fPreview?.candidateName || ''}
        details={f2fPreview?.details || null}
        applicationId={f2fPreview?.applicationId}
        jobTitle={f2fPreview?.jobTitle}
      />
    </ServicePageGuard>
    </AppLayout>
  );
}
